import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App.tsx';
import './index.css';
import { initSentry } from './utils/sentry';
import { initAnalytics } from './utils/analytics';
import { AuthProvider } from './contexts/AuthContext';

// Create a query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000, // 30 seconds
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Initialize Sentry for error tracking.
// Sentry's default integrations already capture window.error and
// unhandledrejection, so the global handlers below intentionally do NOT forward
// to Sentry to avoid double-capture (which was a contributor to ingest 429s).
initSentry();

// Initialize PostHog analytics (prod-only)
initAnalytics();

// Global error handler for production debugging
window.addEventListener('error', (event) => {
  const errorMsg = event.message || String(event.error || '');
  
  // Suppress hydration errors in production - they're expected with prerendering
  if (import.meta.env.PROD && 
      (errorMsg.includes('Minified React error #418') || 
       errorMsg.includes('Minified React error #423') ||
       errorMsg.includes('Hydration') || 
       errorMsg.includes('hydration'))) {
    // Silently ignore hydration errors - React will recover
    return;
  }
  
  const errorInfo = {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    error: event.error,
    stack: event.error?.stack,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href
  };
  
  console.error('🚨 Global Error:', errorInfo);
});

window.addEventListener('unhandledrejection', (event) => {
  const errorInfo = {
    reason: event.reason,
    promise: event.promise,
    timestamp: new Date().toISOString(),
    url: window.location.href
  };
  
  console.error('🚨 Unhandled Promise Rejection:', errorInfo);
});


const rootElement = document.getElementById('root')!;

// Check if content is already prerendered
const hasPrerenderedContent = rootElement.hasChildNodes();

// App component tree
const app = (
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AuthProvider>
          <App />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>
);

if (hasPrerenderedContent) {
  // Try to hydrate pre-rendered content
  // Suppress hydration errors in production - they're expected with prerendering
  hydrateRoot(rootElement, app, {
    onRecoverableError: (error) => {
      // In production, suppress hydration mismatch errors
      // These occur when prerendered HTML doesn't exactly match React's expectations
      if (import.meta.env.PROD) {
        const errorMsg = String(error?.message || error || '');
        if (errorMsg.includes('418') || errorMsg.includes('423') || 
            errorMsg.includes('Hydration') || errorMsg.includes('hydration')) {
          // Silently handle - React will recover by re-rendering
          return;
        }
      }
      // Log other errors
      console.error('Recoverable error during hydration:', error);
    },
  });
} else {
  // Regular render for development or non-prerendered builds
  createRoot(rootElement).render(app);
}